import type { OfferFilters } from '../../types/api';

import type { JobFilters } from './JobFiltersModal';

export type SearchTab = 'jobs' | 'exhibitors';

/**
 * The search page's state as it appears in the URL, so a filtered search can be
 * bookmarked and shared, e.g.
 * `/search?category=Trgovina&position=Prodaja%20(Trgovina)&exhibitor=dm&location=cijela%20Hrvatska`.
 *
 * Filters travel by the names visitors see (and the slugs other URLs already
 * use), not database ids, so a link reads as what it selects. A filter named
 * more than once repeats the parameter.
 */
export interface SearchUrlState {
  tab: SearchTab;
  q: string;
  /** Pavilion slugs. */
  category: string[];
  /** Job category names. */
  position: string[];
  /** Stand slugs. */
  exhibitor: string[];
  location: string[];
}

const FILTER_PARAMS = ['category', 'position', 'exhibitor', 'location'] as const;

export function readSearchUrl(params: URLSearchParams): SearchUrlState {
  const values = (key: string): string[] =>
    params
      .getAll(key)
      .map((value) => value.trim())
      .filter((value) => value !== '');

  return {
    tab: params.get('tab') === 'exhibitors' ? 'exhibitors' : 'jobs',
    q: params.get('q') ?? '',
    category: values('category'),
    position: values('position'),
    exhibitor: values('exhibitor'),
    location: values('location'),
  };
}

/** Empty values are left out, so an unfiltered search is plain `/search`. */
export function writeSearchUrl(state: SearchUrlState): URLSearchParams {
  const params = new URLSearchParams();

  if (state.tab === 'exhibitors') {
    params.set('tab', state.tab);
  }

  if (state.q.trim() !== '') {
    params.set('q', state.q);
  }

  for (const key of FILTER_PARAMS) {
    for (const value of state[key]) {
      params.append(key, value);
    }
  }

  return params;
}

/**
 * Resolves the URL's names to the ids GET /offers takes. A name this fair does
 * not have — a stale or hand-edited link — is dropped rather than failing the
 * search.
 */
export function filtersFromUrl(state: SearchUrlState, available: OfferFilters): JobFilters {
  const ids = <T extends { id: number }>(
    entries: T[],
    names: string[],
    nameOf: (entry: T) => string,
  ): number[] =>
    names.flatMap((name) => {
      const match = entries.find((entry) => nameOf(entry) === name);

      return match === undefined ? [] : [match.id];
    });

  return {
    pavilionIds: ids(available.pavilions, state.category, (pavilion) => pavilion.slug),
    categoryIds: ids(available.categories, state.position, (category) => category.name),
    standIds: ids(available.stands, state.exhibitor, (stand) => stand.slug),
    locations: state.location.filter((location) => available.locations.includes(location)),
  };
}

/** The reverse of `filtersFromUrl`, for writing the modal's selection back. */
export function filtersToUrl(
  filters: JobFilters,
  available: OfferFilters,
): Pick<SearchUrlState, 'category' | 'position' | 'exhibitor' | 'location'> {
  const names = <T extends { id: number }>(
    entries: T[],
    selected: number[],
    nameOf: (entry: T) => string,
  ): string[] =>
    selected.flatMap((id) => {
      const match = entries.find((entry) => entry.id === id);

      return match === undefined ? [] : [nameOf(match)];
    });

  return {
    category: names(available.pavilions, filters.pavilionIds, (pavilion) => pavilion.slug),
    position: names(available.categories, filters.categoryIds, (category) => category.name),
    exhibitor: names(available.stands, filters.standIds, (stand) => stand.slug),
    location: filters.locations,
  };
}
